/**
 * Component: Article table scroll
 * Wraps every table in the article body in a horizontal scroll container, so a wide table
 * scrolls on a phone instead of squeezing its columns. The body is merchant content that
 * cannot carry theme classes, which is why the wrapper and the classes are added here.
 */
(() => {
  const TABLE_SELECTOR = "table:not(.rte-reset-table)";
  const HEADING_SELECTOR = "h2, h3, h4, h5, h6";
  // Inline CMS blocks and table cells carry their own headings, which do not title the copy.
  const FOREIGN_HEADING_SCOPE = "[data-section-id], table";
  const WRAPPER_CLASS = "main-article__table-scroll";
  const TABLE_CLASS = "main-article__table";
  const COLUMNS_PROPERTY = "--main-article-table-columns";

  class ArticleTableScroll extends HTMLElement {
    connectedCallback() {
      this.fallbackLabel = this.dataset.tableLabel;
      this.labels = new WeakMap();
      this.resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => this.updateRegion(entry.target));
      });

      let lastHeading = null;
      this.querySelectorAll(`${HEADING_SELECTOR}, ${TABLE_SELECTOR}`).forEach((element) => {
        if (element.matches(HEADING_SELECTOR)) {
          // Imported copy has empty headings used as spacers.
          if (element.textContent.trim() && !this.isForeignHeading(element)) lastHeading = element;
          return;
        }

        const wrapper = element.classList.contains(TABLE_CLASS)
          ? element.parentElement
          : this.wrap(element);
        this.labels.set(wrapper, this.labelFor(element, lastHeading));
        this.resizeObserver.observe(wrapper);
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    wrap(table) {
      const wrapper = document.createElement("div");
      wrapper.className = WRAPPER_CLASS;
      wrapper.style.setProperty(COLUMNS_PROPERTY, this.countColumns(table));

      table.classList.add(TABLE_CLASS);
      table.before(wrapper);
      wrapper.append(table);
      return wrapper;
    }

    // The article section itself carries data-section-id, so only look inside this element.
    isForeignHeading(heading) {
      const scope = heading.parentElement.closest(FOREIGN_HEADING_SCOPE);
      return Boolean(scope) && this.contains(scope);
    }

    labelFor(table, heading) {
      const caption = table.caption?.textContent.trim();
      return caption || heading?.textContent.trim() || this.fallbackLabel;
    }

    countColumns(table) {
      return Math.max(
        1,
        ...Array.from(table.rows, (row) =>
          Array.from(row.cells).reduce((total, cell) => total + cell.colSpan, 0),
        ),
      );
    }

    // A named, focusable region only while it scrolls: otherwise every table would add a
    // landmark and a tab stop that lead nowhere.
    updateRegion(wrapper) {
      if (wrapper.scrollWidth > wrapper.clientWidth) {
        wrapper.setAttribute("role", "region");
        wrapper.setAttribute("aria-label", this.labels.get(wrapper));
        wrapper.setAttribute("tabindex", "0");
      } else {
        wrapper.removeAttribute("role");
        wrapper.removeAttribute("aria-label");
        wrapper.removeAttribute("tabindex");
      }
    }
  }

  if (!customElements.get("article-table-scroll")) {
    customElements.define("article-table-scroll", ArticleTableScroll);
  }
})();
